import React, { useEffect, useMemo, useState, Component } from 'react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, rectSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { motion, AnimatePresence } from 'framer-motion';
import { TrendingUp, TrendingDown, Minus, AlertTriangle, CheckCircle2, Users, Zap, Search, Filter, GitPullRequestIcon, GitCommitIcon, XCircleIcon, GithubIcon, GitBranchIcon, PlayCircleIcon, Settings2, X, GripHorizontal, Plus, RotateCcw, Info } from 'lucide-react';
import { useProjectContext } from '../../hooks/useProjectContext';
import { useGithubData } from '../../hooks/useGithubData';
import { Card } from '../ui/Card';
import { RefreshButton } from './RefreshButton';
import { useWidgetLayout } from '../../hooks/useWidgetLayout';
import { WidgetCatalog } from './WidgetCatalog';
import { widgetRegistry, WidgetDefinition } from '../../data/widgetRegistry';
import { SprintBurndown } from './SprintBurndown';
import { SprintHealthScore } from './SprintHealthScore';
import { ActivityFeed } from './ActivityFeed';
import { TodoListWidget } from '../widgets/TodoListWidget';
import { PersonalNotesWidget } from '../widgets/PersonalNotesWidget';
import { CalendarWidget } from '../widgets/CalendarWidget';
import { RemindersWidget } from '../widgets/RemindersWidget';
// --- Status Config ---
const statusConfig = {
  active: {
    label: 'Activo',
    dot: 'bg-emerald-400',
    badge: 'bg-emerald-950/40 border-emerald-900/40 text-emerald-400'
  },
  'at-risk': {
    label: 'En riesgo',
    dot: 'bg-amber-400',
    badge: 'bg-amber-950/40 border-amber-900/40 text-amber-400'
  },
  'on-hold': {
    label: 'En pausa',
    dot: 'bg-slate-500',
    badge: 'bg-slate-800 border-slate-700 text-slate-400'
  },
  completed: {
    label: 'Completado',
    dot: 'bg-blue-400',
    badge: 'bg-blue-950/40 border-blue-900/40 text-blue-400'
  }
};
// --- Sortable Widget Wrapper ---
interface SortableWidgetProps {
  id: string;
  widgetDef: WidgetDefinition;
  editMode: boolean;
  onRemove: (id: string) => void;
  children: React.ReactNode;
}
function SortableWidget({
  id,
  widgetDef,
  editMode,
  onRemove,
  children
}: SortableWidgetProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id
  });
  // Responsive column span classes instead of inline style
  // This prevents span overflow on narrow screens
  const colSpanClass = widgetDef.cols === 4 ? 'col-span-1 md:col-span-2 lg:col-span-4' : widgetDef.cols === 3 ? 'col-span-1 md:col-span-2 lg:col-span-3' : widgetDef.cols === 2 ? 'col-span-1 md:col-span-2' : 'col-span-1';
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : 1,
    opacity: isDragging ? 0.5 : 1
  };
  return <div ref={setNodeRef} style={style} className={`relative group h-full ${colSpanClass}`} data-id="element-2158">
      {editMode && <div className="absolute -top-3 -right-3 z-20 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-2159">
          <button onClick={() => onRemove(id)} className="p-1.5 bg-red-500 hover:bg-red-600 text-white rounded-full shadow-lg transition-colors" title="Quitar widget" data-id="element-2160">
            <X size={14} data-id="element-2161" />
          </button>
        </div>}

      <motion.div animate={editMode ? {
      rotate: [-0.5, 0.5, -0.5],
      transition: {
        repeat: Infinity,
        duration: 0.3
      }
    } : {
      rotate: 0
    }} className={`h-full ${editMode ? 'ring-2 ring-indigo-500/50 rounded-xl cursor-grab active:cursor-grabbing' : ''}`} {...editMode ? attributes : {}} {...editMode ? listeners : {}} data-id="element-2162">
        {editMode && <div className="absolute top-2 left-1/2 -translate-x-1/2 z-20 p-1 bg-slate-800/80 backdrop-blur-sm rounded-md border border-slate-700 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" data-id="element-2163">
            <GripHorizontal size={16} className="text-slate-400" data-id="element-2164" />
          </div>}
        <div className={editMode ? 'pointer-events-none' : ''} data-id="element-2165">{children}</div>
      </motion.div>
    </div>;
}
// --- Main Dashboard Component ---
export function GlobalDashboard() {
  const {
    projects,
    setActiveProject,
    setIsGlobalView
  } = useProjectContext();
  const {
    repos,
    prs,
    commits,
    branches,
    workflowRuns,
    loading: githubLoading,
    error: githubError
  } = useGithubData(undefined, true);
  const {
    layout,
    isFirstTime,
    hasNewWidgets,
    newWidgetCount,
    editMode,
    setEditMode,
    addWidget,
    removeWidget,
    moveWidget,
    resetToDefault,
    dismissFirstTime,
    markAllSeen
  } = useWidgetLayout();
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  // --- Computed Metrics ---
  const safeProjects = Array.isArray(projects) ? projects : [];
  const filteredProjects = useMemo(() => {
    return safeProjects.filter(p => {
      const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase()) || p.key.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === 'all' || p.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [safeProjects, searchQuery, statusFilter]);
  const totalTeamSize = safeProjects.reduce((s, p) => s + (p.teamSize || 0), 0);
  const avgVelocity = safeProjects.length > 0 ? Math.round(safeProjects.reduce((s, p) => s + (p.velocity || 0), 0) / safeProjects.length) : 0;
  const avgCompletion = safeProjects.length > 0 ? Math.round(safeProjects.reduce((s, p) => s + (p.completion || 0), 0) / safeProjects.length) : 0;
  const atRiskCount = safeProjects.filter(p => p.status === 'at-risk').length;
  const totalIssues = safeProjects.reduce((s, p) => s + (Array.isArray(p.issues) ? p.issues.length : 0), 0);
  const doneIssues = safeProjects.reduce((s, p) => s + (Array.isArray(p.issues) ? p.issues.filter(i => i.status === 'Done').length : 0), 0);
  const openPRs = useMemo(() => prs.filter(p => p.state === 'open').length, [prs]);
  const failedActions = useMemo(() => workflowRuns.filter(r => r.conclusion === 'failure').length, [workflowRuns]);
  const recentCommits = commits.length;
  const activeRepos = repos.length;
  // --- DndKit Setup ---
  const sensors = useSensors(useSensor(PointerSensor, {
    activationConstraint: {
      distance: 5
    }
  }), useSensor(KeyboardSensor, {
    coordinateGetter: sortableKeyboardCoordinates
  }));
  const handleDragEnd = (event: DragEndEvent) => {
    const {
      active,
      over
    } = event;
    if (over && active.id !== over.id) {
      const oldIndex = layout.indexOf(active.id as string);
      const newIndex = layout.indexOf(over.id as string);
      moveWidget(oldIndex, newIndex);
    }
  };
  // --- Widget Renderers ---
  const renderWidget = (id: string) => {
    switch (id) {
      case 'global-kpis':
        return <div className="grid grid-cols-2 md:grid-cols-4 gap-4 h-full" data-id="element-2166">
            {[{
            label: 'Proyectos Activos',
            value: projects.filter(p => p.status === 'active').length,
            sub: `de ${projects.length} total`,
            icon: CheckCircle2,
            color: 'text-emerald-400'
          }, {
            label: 'Equipo Total',
            value: totalTeamSize,
            sub: 'personas',
            icon: Users,
            color: 'text-blue-400'
          }, {
            label: 'Velocidad Promedio',
            value: `${avgVelocity} pts`,
            sub: 'por sprint',
            icon: Zap,
            color: 'text-indigo-400'
          }, {
            label: 'Completion Rate',
            value: `${avgCompletion}%`,
            sub: 'promedio global',
            icon: TrendingUp,
            color: 'text-violet-400'
          }].map(({
            label,
            value,
            sub,
            icon: Icon,
            color
          }) => <Card key={label} className="p-5 bg-slate-900 border-slate-800 h-full flex flex-col justify-between" data-id="element-2167">
                <div data-id="element-2168">
                  <div className="flex items-start justify-between mb-3" data-id="element-2169">
                    <p className="text-xs text-slate-500 font-medium" data-id="element-2170">
                      {label}
                    </p>
                    <Icon size={16} className={color} data-id="element-2171" />
                  </div>
                  <p className="text-2xl font-bold text-slate-100" data-id="element-2172">{value}</p>
                </div>
                <p className="text-xs text-slate-600 mt-2" data-id="element-2173">{sub}</p>
              </Card>)}
          </div>;
      case 'github-activity':
        return <div className="h-full flex flex-col" data-id="element-2174">
            <div className="flex items-center gap-2 mb-4" data-id="element-2175">
              <GithubIcon size={16} className="text-slate-400" data-id="element-2176" />
              <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider" data-id="element-2177">
                GitHub Activity
              </h3>
              {githubLoading && <span className="text-[10px] text-indigo-400 bg-indigo-950/40 border border-indigo-900/40 px-2 py-0.5 rounded-full animate-pulse" data-id="element-2178">
                  Cargando...
                </span>}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 flex-1" data-id="element-2179">
              {[{
              label: 'Open PRs',
              value: openPRs,
              sub: 'en la organización',
              icon: GitPullRequestIcon,
              color: 'text-emerald-400',
              accent: openPRs > 10 ? 'text-amber-400' : 'text-emerald-400'
            }, {
              label: 'Commits Recientes',
              value: recentCommits,
              sub: 'últimos commits',
              icon: GitCommitIcon,
              color: 'text-blue-400',
              accent: 'text-blue-400'
            }, {
              label: 'Actions Fallidas',
              value: failedActions,
              sub: 'requieren atención',
              icon: XCircleIcon,
              color: 'text-red-400',
              accent: failedActions > 0 ? 'text-red-400' : 'text-emerald-400'
            }, {
              label: 'Repositorios',
              value: activeRepos,
              sub: 'en la org',
              icon: GithubIcon,
              color: 'text-slate-400',
              accent: 'text-slate-300'
            }].map(({
              label,
              value,
              sub,
              icon: Icon,
              color,
              accent
            }) => <Card key={label} className="p-5 bg-slate-900 border-slate-800 h-full flex flex-col justify-between" data-id="element-2180">
                  <div data-id="element-2181">
                    <div className="flex items-start justify-between mb-3" data-id="element-2182">
                      <p className="text-xs text-slate-500 font-medium" data-id="element-2183">
                        {label}
                      </p>
                      <Icon size={16} className={color} data-id="element-2184" />
                    </div>
                    <p className={`text-2xl font-bold ${accent}`} data-id="element-2185">
                      {githubLoading ? '—' : value}
                    </p>
                  </div>
                  <p className="text-xs text-slate-600 mt-2" data-id="element-2186">{sub}</p>
                </Card>)}
            </div>
          </div>;
      case 'project-cards':
        return <div className="h-full flex flex-col" data-id="element-2187">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4" data-id="element-2188">
              <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider" data-id="element-2189">
                Proyectos ({filteredProjects.length})
              </h3>
              <div className="flex items-center gap-3" data-id="element-2190">
                <div className="relative" data-id="element-2191">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" data-id="element-2192" />

                  <input type="text" placeholder="Buscar proyecto..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="bg-slate-900 border border-slate-700 rounded-lg pl-9 pr-4 py-1.5 text-sm text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/50 w-full sm:w-64 transition-all" data-id="element-2193" />
                </div>
                <div className="flex items-center bg-slate-900 border border-slate-700 rounded-lg p-1" data-id="element-2194">
                  <button onClick={() => setStatusFilter('all')} className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${statusFilter === 'all' ? 'bg-slate-800 text-slate-200' : 'text-slate-500 hover:text-slate-300'}`} data-id="element-2195">
                    Todos
                  </button>
                  <button onClick={() => setStatusFilter('active')} className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${statusFilter === 'active' ? 'bg-emerald-950/50 text-emerald-400' : 'text-slate-500 hover:text-slate-300'}`} data-id="element-2196">
                    Activos
                  </button>
                  <button onClick={() => setStatusFilter('at-risk')} className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${statusFilter === 'at-risk' ? 'bg-amber-950/50 text-amber-400' : 'text-slate-500 hover:text-slate-300'}`} data-id="element-2197">
                    En Riesgo
                  </button>
                </div>
              </div>
            </div>

            {filteredProjects.length === 0 ? <div className="text-center py-12 bg-slate-900/50 border border-slate-800 border-dashed rounded-xl flex-1 flex flex-col items-center justify-center" data-id="element-2198">
                <p className="text-slate-500 text-sm" data-id="element-2199">
                  No se encontraron proyectos que coincidan con los filtros.
                </p>
                <button onClick={() => {
              setSearchQuery('');
              setStatusFilter('all');
            }} className="mt-2 text-indigo-400 text-xs hover:underline" data-id="element-2200">
                  Limpiar filtros
                </button>
              </div> : <div className="grid grid-cols-1 gap-4 overflow-y-auto pr-2 custom-scrollbar max-h-[500px]" data-id="element-2201">
                {filteredProjects.map(project => {
              const cfg = statusConfig[project.status as keyof typeof statusConfig] || statusConfig.active;
              const projectIssues = Array.isArray(project.issues) ? project.issues : [];
              const sprintDone = projectIssues.filter(i => i.status === 'Done').length;
              const sprintTotal = projectIssues.length;
              const blockers = projectIssues.filter(i => i.priority === 'High' && i.status === 'To Do').length;
              return <Card key={project.id} className="bg-slate-900 border-slate-800 hover:border-slate-700 transition-all cursor-pointer group shrink-0" onClick={() => {
                if (editMode) return;
                setActiveProject(project.id);
                setIsGlobalView(false);
              }} data-id="element-2202">
                      <div className="p-5" data-id="element-2203">
                        <div className="flex items-start justify-between gap-4" data-id="element-2204">
                          <div className="flex items-start gap-4 flex-1 min-w-0" data-id="element-2205">
                            <div className={`w-10 h-10 rounded-xl ${project.color || 'bg-indigo-500'} flex items-center justify-center text-white font-bold text-sm shrink-0 shadow-lg`} data-id="element-2206">
                              {project.key.slice(0, 3)}
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-2207">
                              <div className="flex items-center gap-2 mb-1" data-id="element-2208">
                                <h4 className="text-sm font-bold text-slate-100 group-hover:text-indigo-300 transition-colors" data-id="element-2209">
                                  {project.name}
                                </h4>
                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${cfg.badge}`} data-id="element-2210">
                                  {cfg.label}
                                </span>
                              </div>
                              <p className="text-xs text-slate-500 truncate" data-id="element-2211">
                                {project.description}
                              </p>
                              <div className="flex items-center gap-3 mt-2 text-[11px] text-slate-500" data-id="element-2212">
                                <span data-id="element-2213">{project.team}</span>
                                <span data-id="element-2214">·</span>
                                <span data-id="element-2215">{project.teamSize} personas</span>
                                <span data-id="element-2216">·</span>
                                <span className="font-mono" data-id="element-2217">{project.key}</span>
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-6 shrink-0" data-id="element-2218">
                            <div className="text-right hidden md:block" data-id="element-2219">
                              <p className="text-[10px] text-slate-600 mb-1" data-id="element-2220">
                                {project.sprint?.name?.split(':')[0] || 'Sprint'}
                              </p>
                              <div className="flex items-center gap-2" data-id="element-2221">
                                <div className="w-24 h-1.5 bg-slate-700 rounded-full overflow-hidden" data-id="element-2222">
                                  <div className={`h-full rounded-full transition-all ${project.status === 'at-risk' ? 'bg-amber-500' : 'bg-indigo-500'}`} style={{
                              width: `${project.sprint?.progress || 0}%`
                            }} data-id="element-2223" />
                                </div>
                                <span className="text-xs font-semibold text-slate-300" data-id="element-2224">
                                  {project.sprint?.progress || 0}%
                                </span>
                              </div>
                              <p className="text-[10px] text-slate-600 mt-1" data-id="element-2225">
                                {project.sprint?.completedPoints || 0}/
                                {project.sprint?.totalPoints || 0} pts
                              </p>
                            </div>

                            <div className="grid grid-cols-2 gap-x-4 gap-y-1 hidden lg:grid" data-id="element-2226">
                              <div data-id="element-2227">
                                <p className="text-[10px] text-slate-600" data-id="element-2228">
                                  Velocidad
                                </p>
                                <p className="text-xs font-semibold text-slate-300" data-id="element-2229">
                                  {project.velocity || 0} pts
                                </p>
                              </div>
                              <div data-id="element-2230">
                                <p className="text-[10px] text-slate-600" data-id="element-2231">
                                  Completion
                                </p>
                                <p className={`text-xs font-semibold ${(project.completion || 0) >= 80 ? 'text-emerald-400' : (project.completion || 0) >= 60 ? 'text-amber-400' : 'text-red-400'}`} data-id="element-2232">
                                  {project.completion || 0}%
                                </p>
                              </div>
                              <div data-id="element-2233">
                                <p className="text-[10px] text-slate-600" data-id="element-2234">
                                  Issues
                                </p>
                                <p className="text-xs font-semibold text-slate-300" data-id="element-2235">
                                  {sprintDone}/{sprintTotal}
                                </p>
                              </div>
                              <div data-id="element-2236">
                                <p className="text-[10px] text-slate-600" data-id="element-2237">
                                  Bloqueadores
                                </p>
                                <p className={`text-xs font-semibold ${blockers > 0 ? 'text-red-400' : 'text-emerald-400'}`} data-id="element-2238">
                                  {blockers > 0 ? blockers : '✓'}
                                </p>
                              </div>
                            </div>

                            <div className="text-slate-700 group-hover:text-indigo-400 transition-colors" data-id="element-2239">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" data-id="element-2240">
                                <path d="M5 12h14M12 5l7 7-7 7" data-id="element-2241" />
                              </svg>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Card>;
            })}
              </div>}
          </div>;
      case 'cross-project-summary':
        return <div className="grid grid-cols-1 md:grid-cols-3 gap-4 h-full" data-id="element-2242">
            <Card className="p-4 bg-slate-900 border-slate-800 h-full flex flex-col justify-between" data-id="element-2243">
              <div data-id="element-2244">
                <p className="text-xs text-slate-500 mb-2" data-id="element-2245">
                  Issues en progreso (global)
                </p>
                <p className="text-2xl font-bold text-blue-400" data-id="element-2246">
                  {safeProjects.reduce((s, p) => s + (Array.isArray(p.issues) ? p.issues.filter(i => i.status === 'In Progress').length : 0), 0)}
                </p>
              </div>
              <p className="text-xs text-slate-600 mt-2" data-id="element-2247">
                en todos los proyectos
              </p>
            </Card>
            <Card className="p-4 bg-slate-900 border-slate-800 h-full flex flex-col justify-between" data-id="element-2248">
              <div data-id="element-2249">
                <p className="text-xs text-slate-500 mb-2" data-id="element-2250">
                  Bloqueadores activos
                </p>
                <p className="text-2xl font-bold text-red-400" data-id="element-2251">
                  {safeProjects.reduce((s, p) => s + (Array.isArray(p.issues) ? p.issues.filter(i => i.priority === 'High' && i.status === 'To Do').length : 0), 0)}
                </p>
              </div>
              <p className="text-xs text-slate-600 mt-2" data-id="element-2252">requieren atención</p>
            </Card>
            <Card className="p-4 bg-slate-900 border-slate-800 h-full flex flex-col justify-between" data-id="element-2253">
              <div data-id="element-2254">
                <p className="text-xs text-slate-500 mb-2" data-id="element-2255">
                  Completados este sprint
                </p>
                <p className="text-2xl font-bold text-emerald-400" data-id="element-2256">
                  {doneIssues}
                </p>
              </div>
              <p className="text-xs text-slate-600 mt-2" data-id="element-2257">
                de {totalIssues} issues totales
              </p>
            </Card>
          </div>;
      case 'sprint-burndown':
        // Mock data for global view
        const mockBurndownData = [{
          day: 0,
          date: '2023-10-01',
          label: 'Oct 1',
          ideal: 100,
          actual: 100
        }, {
          day: 1,
          date: '2023-10-02',
          label: 'Oct 2',
          ideal: 90,
          actual: 95
        }, {
          day: 2,
          date: '2023-10-03',
          label: 'Oct 3',
          ideal: 80,
          actual: 85
        }, {
          day: 3,
          date: '2023-10-04',
          label: 'Oct 4',
          ideal: 70,
          actual: 60
        }, {
          day: 4,
          date: '2023-10-05',
          label: 'Oct 5',
          ideal: 60,
          actual: 55
        }, {
          day: 5,
          date: '2023-10-06',
          label: 'Oct 6',
          ideal: 50,
          actual: null
        }];
        return <div className="h-[300px]" data-id="element-2258">
            <SprintBurndown data={mockBurndownData} data-id="element-2259" />
          </div>;
      case 'sprint-health':
        return <Card className="h-full bg-slate-900 border-slate-800 p-5" data-id="element-2260">
            <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4" data-id="element-2261">
              Salud Global de Sprints
            </h3>
            <SprintHealthScore score={avgCompletion} metrics={{
            completion: avgCompletion,
            completedPts: safeProjects.reduce((s, p) => s + (p.sprint?.completedPoints || 0), 0),
            totalPts: safeProjects.reduce((s, p) => s + (p.sprint?.totalPoints || 0), 0),
            blockers: safeProjects.reduce((s, p) => s + (Array.isArray(p.issues) ? p.issues.filter(i => i.priority === 'High' && i.status === 'To Do').length : 0), 0),
            unestimated: 5,
            daysRemaining: 4,
            totalDays: 14,
            doneCount: doneIssues,
            inProgressCount: safeProjects.reduce((s, p) => s + (Array.isArray(p.issues) ? p.issues.filter(i => i.status === 'In Progress').length : 0), 0),
            totalIssues: totalIssues
          }} data-id="element-2262" />
          </Card>;
      case 'activity-feed':
        return <Card className="h-[400px] bg-slate-900 border-slate-800 p-4" data-id="element-2263">
            <ActivityFeed data-id="element-2264" />
          </Card>;
      case 'blockers-list':
        // Mock blockers list
        const blockers = safeProjects.flatMap(p => (Array.isArray(p.issues) ? p.issues : []).filter(i => i.priority === 'High' && i.status === 'To Do').map(i => ({
          ...i,
          projectKey: p.key
        }))).slice(0, 5);
        return <Card className="h-full bg-slate-900 border-slate-800 p-5 overflow-y-auto custom-scrollbar max-h-[400px]" data-id="element-2265">
            <div className="flex items-center gap-2 mb-4" data-id="element-2266">
              <AlertTriangle size={16} className="text-red-400" data-id="element-2267" />
              <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider" data-id="element-2268">
                Bloqueadores Activos
              </h3>
            </div>
            {blockers.length === 0 ? <div className="text-center py-8 text-slate-500 text-sm" data-id="element-2269">
                No hay bloqueadores activos. ¡Excelente!
              </div> : <div className="space-y-3" data-id="element-2270">
                {blockers.map((b, i) => <div key={i} className="p-3 bg-red-950/20 border border-red-900/30 rounded-lg" data-id="element-2271">
                    <div className="flex items-start justify-between gap-2" data-id="element-2272">
                      <p className="text-sm font-medium text-slate-200 line-clamp-2" data-id="element-2273">
                        {b.summary}
                      </p>
                      <span className="text-[10px] font-mono text-red-400 bg-red-950/50 px-1.5 py-0.5 rounded shrink-0" data-id="element-2274">
                        {b.key}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mt-2 text-xs text-slate-500" data-id="element-2275">
                      <span className="flex items-center gap-1" data-id="element-2276">
                        <div className="w-4 h-4 rounded-full bg-slate-800 flex items-center justify-center text-[8px] font-bold text-slate-300" data-id="element-2277">
                          {b.assignee?.initials || '?'}
                        </div>
                        {b.assignee?.name || 'Sin asignar'}
                      </span>
                      <span data-id="element-2278">{b.projectKey}</span>
                    </div>
                  </div>)}
              </div>}
          </Card>;
      case 'team-workload':
        return <Card className="h-full bg-slate-900 border-slate-800 p-5" data-id="element-2279">
            <div className="flex items-center gap-2 mb-4" data-id="element-2280">
              <Users size={16} className="text-blue-400" data-id="element-2281" />
              <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider" data-id="element-2282">
                Carga del Equipo (Top 5)
              </h3>
            </div>
            <div className="space-y-4" data-id="element-2283">
              {[{
              name: 'Ana García',
              initials: 'AG',
              load: 85,
              pts: 24
            }, {
              name: 'Carlos Ruiz',
              initials: 'CR',
              load: 70,
              pts: 18
            }, {
              name: 'Sofia Chen',
              initials: 'SC',
              load: 95,
              pts: 32
            }, {
              name: 'Miguel Silva',
              initials: 'MS',
              load: 40,
              pts: 12
            }, {
              name: 'Laura Park',
              initials: 'LP',
              load: 60,
              pts: 16
            }].sort((a, b) => b.load - a.load).map((member, i) => <div key={i} className="flex items-center gap-3" data-id="element-2284">
                    <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center text-xs font-bold text-slate-300 shrink-0" data-id="element-2285">
                      {member.initials}
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-2286">
                      <div className="flex justify-between text-xs mb-1" data-id="element-2287">
                        <span className="font-medium text-slate-200 truncate" data-id="element-2288">
                          {member.name}
                        </span>
                        <span className="text-slate-500" data-id="element-2289">{member.pts} pts</span>
                      </div>
                      <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden" data-id="element-2290">
                        <div className={`h-full rounded-full ${member.load > 90 ? 'bg-red-500' : member.load > 75 ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{
                    width: `${member.load}%`
                  }} data-id="element-2291" />
                      </div>
                    </div>
                  </div>)}
            </div>
          </Card>;
      case 'todo-list':
        return <TodoListWidget data-id="element-2292" />;
      case 'personal-notes':
        return <PersonalNotesWidget data-id="element-2293" />;
      case 'calendar':
        return <CalendarWidget data-id="element-2294" />;
      case 'reminders':
        return <RemindersWidget data-id="element-2295" />;
      default:
        return <Card className="h-full bg-slate-900 border-slate-800 p-5 flex items-center justify-center text-slate-500" data-id="element-2296">
            Widget no encontrado: {id}
          </Card>;
    }
  };
  return <div className="flex-1 w-full h-full overflow-y-auto p-4 md:p-8 animate-in fade-in duration-300 relative" data-id="element-2297">
      <div className="max-w-6xl mx-auto space-y-8" data-id="element-2298">
        {/* Global header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-2299">
          <div data-id="element-2300">
            <h2 className="text-2xl font-bold text-slate-100" data-id="element-2301">Vista Global</h2>
            <p className="text-sm text-slate-500 mt-1" data-id="element-2302">
              {projects.length} proyectos activos · {totalTeamSize} personas ·{' '}
              {layout.length} widgets
            </p>
          </div>
          <div className="flex items-center gap-3" data-id="element-2303">
            {atRiskCount > 0 && <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-amber-950/40 border border-amber-900/40 rounded-lg text-xs text-amber-400" data-id="element-2304">
                <AlertTriangle size={13} data-id="element-2305" />
                {atRiskCount} proyecto{atRiskCount > 1 ? 's' : ''} en riesgo
              </div>}

            {editMode ? <div className="flex items-center gap-2" data-id="element-2306">
                <button onClick={resetToDefault} className="flex items-center gap-2 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 text-sm font-medium rounded-lg transition-colors" data-id="element-2307">
                  <RotateCcw size={14} data-id="element-2308" />
                  <span className="hidden sm:inline" data-id="element-2309">Restaurar</span>
                </button>
                <button onClick={() => {
              setCatalogOpen(true);
              markAllSeen();
            }} className="flex items-center gap-2 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium rounded-lg transition-colors" data-id="element-2310">
                  <Plus size={14} data-id="element-2311" />
                  <span className="hidden sm:inline" data-id="element-2312">Agregar Widget</span>
                </button>
                <button onClick={() => setEditMode(false)} className="flex items-center gap-2 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium rounded-lg transition-colors" data-id="element-2313">
                  <CheckCircle2 size={14} data-id="element-2314" />
                  <span className="hidden sm:inline" data-id="element-2315">Listo</span>
                </button>
              </div> : <div className="flex items-center gap-2 relative" data-id="element-2316">
                <RefreshButton size="sm" label="Refresh" data-id="element-2317" />
                <button onClick={() => {
              setEditMode(true);
              dismissFirstTime();
            }} className="relative flex items-center gap-2 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 text-sm font-medium rounded-lg transition-colors" data-id="element-2318">
                  <Settings2 size={14} data-id="element-2319" />
                  <span className="hidden sm:inline" data-id="element-2320">Personalizar</span>
                  {hasNewWidgets && <span className="absolute -top-1 -right-1 flex h-3 w-3" data-id="element-2321">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75" data-id="element-2322"></span>
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-indigo-500" data-id="element-2323"></span>
                    </span>}
                </button>

                {/* First-time tooltip */}
                <AnimatePresence data-id="element-2324">
                  {isFirstTime && <motion.div initial={{
                opacity: 0,
                y: 10,
                scale: 0.95
              }} animate={{
                opacity: 1,
                y: 0,
                scale: 1
              }} exit={{
                opacity: 0,
                y: 10,
                scale: 0.95
              }} className="absolute top-full right-0 mt-3 w-64 bg-indigo-600 text-white p-4 rounded-xl shadow-xl z-50" data-id="element-2325">
                      <div className="absolute -top-2 right-6 w-4 h-4 bg-indigo-600 transform rotate-45" data-id="element-2326"></div>
                      <div className="relative z-10" data-id="element-2327">
                        <div className="flex items-start gap-3 mb-2" data-id="element-2328">
                          <Info size={18} className="shrink-0 text-indigo-200" data-id="element-2329" />

                          <h4 className="font-bold text-sm" data-id="element-2330">
                            ¡Nuevo Dashboard!
                          </h4>
                        </div>
                        <p className="text-xs text-indigo-100 mb-3 leading-relaxed" data-id="element-2331">
                          Ahora podés personalizar tu vista. Arrastrá, sacá y
                          agregá widgets a tu gusto.
                        </p>
                        <button onClick={dismissFirstTime} className="w-full py-1.5 bg-indigo-700 hover:bg-indigo-800 rounded-lg text-xs font-semibold transition-colors" data-id="element-2332">
                          Entendido
                        </button>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>}
          </div>
        </div>

        {/* Edit Mode Banner */}
        <AnimatePresence data-id="element-2333">
          {editMode && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} className="overflow-hidden" data-id="element-2334">
              <div className="bg-indigo-950/30 border border-indigo-500/30 rounded-xl p-4 flex items-center justify-between" data-id="element-2335">
                <p className="text-sm text-indigo-200" data-id="element-2336">
                  <strong className="text-indigo-400" data-id="element-2337">Modo Edición:</strong>{' '}
                  Arrastrá los widgets para reordenarlos, o eliminalos con la X.
                </p>
              </div>
            </motion.div>}
        </AnimatePresence>

        {/* Widget Grid */}
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd} data-id="element-2338">
          <SortableContext items={layout} strategy={rectSortingStrategy} data-id="element-2339">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-2340">
              {layout.map(id => {
              const widgetDef = widgetRegistry.find(w => w.id === id);
              if (!widgetDef) return null;
              return <SortableWidget key={id} id={id} widgetDef={widgetDef} editMode={editMode} onRemove={removeWidget} data-id="element-2341">
                    {renderWidget(id)}
                  </SortableWidget>;
            })}
            </div>
          </SortableContext>
        </DndContext>

        {layout.length === 0 && <div className="text-center py-20 bg-slate-900/50 border border-slate-800 border-dashed rounded-2xl" data-id="element-2342">
            <div className="w-16 h-16 bg-slate-800 rounded-2xl flex items-center justify-center mx-auto mb-4" data-id="element-2343">
              <Settings2 size={24} className="text-slate-500" data-id="element-2344" />
            </div>
            <h3 className="text-lg font-bold text-slate-200 mb-2" data-id="element-2345">
              Dashboard Vacío
            </h3>
            <p className="text-sm text-slate-400 mb-6 max-w-md mx-auto" data-id="element-2346">
              No tenés widgets configurados. Agregá algunos desde el catálogo
              para armar tu vista ideal.
            </p>
            <button onClick={() => {
          setEditMode(true);
          setCatalogOpen(true);
        }} className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold rounded-lg transition-colors" data-id="element-2347">
              Abrir Catálogo
            </button>
          </div>}
      </div>

      <WidgetCatalog open={catalogOpen} onClose={() => setCatalogOpen(false)} onAdd={addWidget} activeWidgets={layout} data-id="element-2348" />
    </div>;
}